import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Items created in this browser session get a badge («Новый», «Сохранён»,
 * «Приглашён»). Each kind keeps only the latest batch, like in the design.
 */
export type RecentKind = 'course' | 'lesson' | 'promo' | 'student';

interface RecentContextValue {
  mark: (kind: RecentKind, ids: string[]) => void;
  isRecent: (kind: RecentKind, id: string) => boolean;
}

const RecentContext = createContext<RecentContextValue | null>(null);

export function RecentProvider({ children }: { children: ReactNode }) {
  const [batches, setBatches] = useState<Partial<Record<RecentKind, ReadonlySet<string>>>>({});

  const mark = useCallback((kind: RecentKind, ids: string[]) => {
    setBatches((prev) => ({ ...prev, [kind]: new Set(ids) }));
  }, []);

  const isRecent = useCallback((kind: RecentKind, id: string) => batches[kind]?.has(id) ?? false, [batches]);

  const value = useMemo(() => ({ mark, isRecent }), [mark, isRecent]);
  return <RecentContext.Provider value={value}>{children}</RecentContext.Provider>;
}

export function useRecent(): RecentContextValue {
  const ctx = useContext(RecentContext);
  if (!ctx) throw new Error('useRecent must be used inside RecentProvider');
  return ctx;
}
